import Dialog from '@mui/material/Dialog'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import React, { useMemo, useState } from 'react'
import Autosuggest, { RenderInputComponentProps } from 'react-autosuggest'
import { useProductSuggestionsLazyQuery } from '@graphql/queries/__generated__/product-suggestion.generated'
import useDebounce from '../../../hooks/useDebounce'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useTranslation } from 'react-i18next'
import { ProductSuggestionFragment } from '@graphql/fragments/__generated__/ProductSuggestion.generated'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { FORM_ERRORS_MAPPING } from '../../../../mappings'
import { Button, Divider, useTheme } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import Avatar from '@mui/material/Avatar'

interface ISearchFormData {
  q: string
}

const getSuggestionValue = (suggestion: ProductSuggestionFragment) => suggestion.text

const renderSuggestion = (suggestion: ProductSuggestionFragment) => (
  <div className='flex items-center'>
    <Avatar
      alt={suggestion.text}
      src={suggestion.specific?.product?.thumbnailUrl}
      variant='rounded'
      sx={{ width: '25px', height: '25px' }}
    />
    <span className='ml-10'>{suggestion.text}</span>
  </div>
)

interface IProps {
  initialQuery: string
  isActive?: boolean

  onSubmitSearch(values: ISearchFormData): Promise<void> | void

  handleToggle(): void
}

const HeaderMobileSearch: React.FC<IProps> = ({ onSubmitSearch, initialQuery, isActive, handleToggle }) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down('sm'))
  const [suggestions, setSuggestions] = useState([])
  const [loadProductSuggestions] = useProductSuggestionsLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: data => setSuggestions(data.productSuggestions),
  })

  const schema = useMemo(
    () =>
      Yup.object().shape<ISearchFormData>({
        q: Yup.string()
          .min(2, FORM_ERRORS_MAPPING(t, 'tooShortWithValue', { min: 3 }))
          .required(FORM_ERRORS_MAPPING(t, 'valueIsRequired')),
      }),
    [t, i18n.language]
  )
  const form = useFormik<ISearchFormData>({
    enableReinitialize: true,
    initialValues: {
      q: initialQuery,
    },
    validationSchema: schema,
    onSubmit: onSubmitSearch,
  })

  const onSuggestionsFetchRequested = useDebounce(({ value }) => {
    void loadProductSuggestions({ variables: { q: value, size: smDown ? 4 : 6 } })
  }, 250)

  const onSuggestionsClearRequested = () => {
    setSuggestions([])
  }

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    return (
      <div {...containerProps}>
        {children}
        <div>
          <Divider />
          <Button variant='text' size='medium' onClick={() => form.handleSubmit()} fullWidth>
            View all results
          </Button>
        </div>
      </div>
    )
  }

  const renderInputComponent = (inputProps: RenderInputComponentProps) => (
    <div className='flex items-center justify-content-center w-full h-full'>
      <div className='h-full flex items-center w-44 justify-center cursor-pointer'>
        <ChevronLeftIcon className='pt-px' onClick={handleToggle} fontSize='large' />
      </div>

      <input {...inputProps} />

      <button className='search-btn flex' type='submit'>
        <span className='capitalize md:uppercase'>{t('button.search', 'Search')}</span>
        <ChevronRightIcon />
      </button>
    </div>
  )
  const onSuggestionSelected = () => {
    form.handleSubmit()
  }

  const inputProps = {
    placeholder: t('placeholder.typeHere', 'Type Here'),
    value: form.values.q,
    onChange: (event: React.FormEvent<any>, { newValue }) => form.setFieldValue('q', newValue),
    onBlur: form.handleBlur,
    name: 'q',
    autoFocus: true,
    className: 'not-mini data-hj-allow',
    id: 'searchInput',
    'aria-label': 'q',
  }

  return (
    <Dialog
      open={isActive}
      onClose={handleToggle}
      fullWidth
      maxWidth={false}
      PaperProps={{
        style: {
          height: 'auto',
          minHeight: 60,
          alignSelf: 'flex-start',
          overflow: 'visible',
        },
      }}
      fullScreen
    >
      <div className='search-bar opened'>
        <form onSubmit={form.handleSubmit}>
          <Autosuggest
            suggestions={suggestions}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            inputProps={inputProps}
            renderInputComponent={renderInputComponent}
            onSuggestionSelected={onSuggestionSelected}
            renderSuggestionsContainer={renderSuggestionsContainer}
          />
        </form>
      </div>
    </Dialog>
  )
}

export default HeaderMobileSearch
